export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'airooki_theme';

export function getStoredTheme(): Theme | null {
  const value = window.localStorage.getItem(STORAGE_KEY);
  return value === 'light' || value === 'dark' ? value : null;
}

export function getSystemTheme(): Theme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
  window.localStorage.setItem(STORAGE_KEY, theme);
}

/** Reflects a theme in the DOM without persisting it as an explicit user choice. */
export function reflectTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
}

export function clearStoredTheme(): void {
  window.localStorage.removeItem(STORAGE_KEY);
}

export function getActiveTheme(): Theme {
  return getStoredTheme() ?? getSystemTheme();
}

/**
 * Follows the OS color scheme live as long as the user hasn't made an explicit
 * choice. Returns an unsubscribe function.
 */
export function watchSystemTheme(onChange: (theme: Theme) => void): () => void {
  const query = window.matchMedia('(prefers-color-scheme: dark)');
  const listener = (e: MediaQueryListEvent) => {
    if (getStoredTheme() === null) {
      const next = e.matches ? 'dark' : 'light';
      reflectTheme(next);
      onChange(next);
    }
  };
  query.addEventListener('change', listener);
  return () => query.removeEventListener('change', listener);
}
